import type { Identifier } from "ra-core";

import { inicioDelDia, inicioDeSemana, sumarDias } from "./fechas";

/**
 * «Resumen de llamadas» (28d, Robert 15/10/2026): por persona, las llamadas registradas con
 * «He llamado» y cuántas de cada resultado. La base (`nevora_resumen_llamadas`) cuenta; aquí,
 * el periodo, el total y los porcentajes.
 */
export type FilaResumen = {
  sale_id: Identifier;
  llamadas: number;
  sin_resultado: number;
  no_contesta: number;
  ni_si_ni_no: number;
  interesado: number;
  quiere_reunion: number;
  no_interesa: number;
  mas_adelante: number;
  /** 37: resultados de la 36 (sin la 37 en la base no vienen: cuentan 0). */
  no_se_presento?: number;
  no_quiere_contacto?: number;
};

/** Columnas de resultado, en el orden en que se enseñan. */
export const COLUMNAS_RESUMEN = [
  "no_contesta",
  "ni_si_ni_no",
  "interesado",
  "quiere_reunion",
  "no_interesa",
  "mas_adelante",
  "no_se_presento",
  "no_quiere_contacto",
  "sin_resultado",
] as const satisfies readonly (keyof FilaResumen)[];

// «Mes» (Robert, 19/10/2026): además de día y semana.
export type VistaResumen = "dia" | "semana" | "mes";

/** Periodo de un día, una semana (lunes a domingo) o un mes que contiene `ref`, en hora local. */
export const periodoResumen = (
  vista: VistaResumen,
  ref: Date,
): { desde: Date; hasta: Date } => {
  if (vista === "mes") {
    return {
      desde: new Date(ref.getFullYear(), ref.getMonth(), 1),
      hasta: new Date(ref.getFullYear(), ref.getMonth() + 1, 1),
    };
  }
  const desde = vista === "dia" ? inicioDelDia(ref) : inicioDeSemana(ref);
  return { desde, hasta: sumarDias(desde, vista === "dia" ? 1 : 7) };
};

/** El día, la semana o el mes anterior (-1) o siguiente (+1). */
export const moverPeriodo = (vista: VistaResumen, ref: Date, paso: 1 | -1) =>
  vista === "mes"
    ? new Date(ref.getFullYear(), ref.getMonth() + paso, 1)
    : sumarDias(ref, (vista === "dia" ? 1 : 7) * paso);

/** Suma de todas las filas (la fila «Total»). */
export const totalResumen = (
  filas: FilaResumen[],
): Omit<FilaResumen, "sale_id"> => {
  const total = {
    llamadas: 0,
    sin_resultado: 0,
    no_contesta: 0,
    ni_si_ni_no: 0,
    interesado: 0,
    quiere_reunion: 0,
    no_interesa: 0,
    mas_adelante: 0,
    no_se_presento: 0,
    no_quiere_contacto: 0,
  };
  for (const f of filas) {
    for (const k of Object.keys(total) as (keyof typeof total)[]) {
      total[k] += Number(f[k]) || 0;
    }
  }
  return total;
};

/** Porcentaje entero sobre las llamadas; sin llamadas, ninguno. */
export const porcentaje = (n: number, llamadas: number): number | null =>
  llamadas > 0 ? Math.round((n / llamadas) * 100) : null;

/** Resultados en los que no se ha hablado con la persona (Robert, 22/10/2026). */
const SIN_HABLAR = ["no_contesta", "no_se_presento"] as const;

/**
 * Llamadas contestadas (Robert, 22/10/2026): aquellas en las que hemos hablado con la persona. Todas
 * menos «No contesta» (en «He visitado» se llama «No estaba el responsable / cerrado»; este resumen
 * solo cuenta «He llamado») y «No se presentó» (se anota con «He llamado», pero no hubo conversación).
 */
export const contestadas = (
  f: Pick<FilaResumen, "llamadas" | "no_contesta" | "no_se_presento">,
): number =>
  Math.max(
    0,
    (Number(f.llamadas) || 0) -
      SIN_HABLAR.reduce((s, k) => s + (Number(f[k]) || 0), 0),
  );

/** Las columnas que llevan además el % sobre las contestadas: las de una conversación. */
export const conPorcentajeContestadas = (
  c: (typeof COLUMNAS_RESUMEN)[number],
) => !(SIN_HABLAR as readonly string[]).includes(c);
